import { useState } from "react";

import { Button, ConfirmationModal } from "@/components/ui";
import type { AttributeReviewMappingRow } from "@/hooks/useAttributeReview.types";
import { isRequiredLevel } from "@/lib/attributeReview/requiredWarning";

type Props = {
  selected: AttributeReviewMappingRow[];
  onApprove: (rows: AttributeReviewMappingRow[]) => void;
  onIgnore: (rows: AttributeReviewMappingRow[]) => void;
  onClear: () => void;
};

const rowsWord = (count: number) => `${count} row${count === 1 ? "" : "s"}`;

export function ReviewBulkBar({
  selected,
  onApprove,
  onIgnore,
  onClear,
}: Props) {
  const [confirm, setConfirm] = useState<"approve" | "ignore" | null>(null);
  if (selected.length === 0) return null;
  const approvable = selected.filter((row) => row.leaf || row.hidden);
  const noPick = selected.length - approvable.length;
  const hasRequired = selected.some((row) =>
    isRequiredLevel(row.requirementLevel),
  );

  return (
    <div
      role="region"
      aria-label="Selected rows"
      className="sticky bottom-0 z-20 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white px-4 py-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
    >
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {selected.length} selected
      </span>
      {noPick > 0 && (
        <span className="text-sm text-slate-500 dark:text-slate-400">
          · {noPick} with no pick, Approve skips it
        </span>
      )}
      {hasRequired && (
        <span className="text-sm text-slate-500 dark:text-slate-400">
          · Required rows cannot be ignored.
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button
          size="sm"
          disabled={approvable.length === 0}
          onClick={() => setConfirm("approve")}
        >
          Approve {approvable.length}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={hasRequired}
          title={hasRequired ? "Required rows cannot be ignored." : undefined}
          onClick={() => setConfirm("ignore")}
        >
          Ignore {selected.length}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear}>
          Clear
        </Button>
      </div>
      <ConfirmationModal
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        variant={confirm === "approve" ? "primary" : "danger"}
        title={
          confirm === "approve"
            ? `Approve ${rowsWord(approvable.length)}?`
            : `Ignore ${rowsWord(selected.length)}?`
        }
        description={
          confirm === "approve"
            ? "This saves them now."
            : "They are left out of the published form. You can undo from the Ignored group."
        }
        confirmLabel={confirm === "approve" ? "Approve" : "Ignore"}
        onConfirm={() => {
          if (confirm === "approve") onApprove(approvable);
          else onIgnore(selected);
          setConfirm(null);
        }}
      />
    </div>
  );
}
